import { Button, Form, Input, Modal, Select, Table, Typography, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { errorMessage, repositoryLabel } from "./ParamTable";
import {
  createFeature,
  deleteFeature,
  fetchFeatureEnvironments,
  fetchFeatures,
  unlinkFeature,
  updateFeature,
} from "../../redux/actions/featureActions";

import { Puces, useUrlParams } from "../utils/PageEntete";
import RowActions, { ACTIONS_COLUMN } from "../utils/RowActions";
import SettingsHeader from "./SettingsHeader";
import { colonneEtat } from "../Supervision/UsageBadges";
import moment from "moment";

const TYPES = [
  { value: "API", label: "API" },
  { value: "licence", label: "licence" },
];

const list = (v) => (Array.isArray(v) ? v : []);
const sansAccent = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const FILTRES = { display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0" };

// Meme gabarit que Configuration > Referentiels > Vues : barre de pastilles a compteur (Catalogue /
// Environnements associes) avec une info secondaire a droite, dans le panneau (pas dans la barre des sous-menus).
function BarreVues({ view, setView, nbCatalogue, nbEnvironnements, info }) {
  return (
    <Puces
      label="Vue"
      value={view}
      onChange={setView}
      items={[
        { key: "catalog", label: "Catalogue", count: nbCatalogue },
        { key: "environments", label: "Environnements associés", count: nbEnvironnements },
      ]}
      extra={
        info && (
          <Typography.Text type="secondary" className="cti-bar-end">
            {info}
          </Typography.Text>
        )
      }
    />
  );
}

const totalEnvironnements = (features) => features.reduce((n, f) => n + (Number(f.environment_count) || 0), 0);


/* Catalogue : declaration (nom, type, modules, depot). Jamais de cle ici. */
function Declare({ data, loading, reload, view, setView, onShowEnvironments }) {
  const [editing, setEditing] = useState(null); // null | {} (ajout) | row
  const [fType, setFType] = useState(undefined);
  const [fModule, setFModule] = useState(undefined);
  const [fNom, setFNom] = useState("");
  // Filtres locaux ; le compteur de la pastille Catalogue suit
  const features = useMemo(() => {
    const q = sansAccent(fNom.trim());
    return data.features.filter(
      (f) =>
        (!fType || f.provider_type === fType) &&
        (!fModule || list(f.modules).some((m) => m.oid === fModule)) &&
        (!q || sansAccent(f.name).includes(q))
    );
  }, [data.features, fType, fModule, fNom]);
  const [form] = Form.useForm();

  const openForm = (row) => {
    form.resetFields();
    form.setFieldsValue(
      row
        ? {
            name: row.name,
            provider_type: row.provider_type || undefined,
            module_ids: list(row.modules).map((m) => m.oid),
            repository_id: row.repository ? row.repository.oid : undefined,
          }
        : {}
    );
    setEditing(row || {});
  };

  const handleSubmit = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch (e) {
      return;
    }
    const payload = {
      name: v.name.trim(),
      provider_type: v.provider_type,
      module_ids: v.module_ids,
      repository_id: v.repository_id || null,
    };
    try {
      if (editing.oid) {
        await updateFeature(editing.oid, payload);
      } else {
        await createFeature(payload);
      }
      message.success("Enregistré");
      setEditing(null);
      reload();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteFeature(row.oid);
      message.success("Supprimé");
      reload();
    } catch (e) {
      // 409 : fonctionnalite liee a des environnements (message de l'API)
      message.error(errorMessage(e));
    }
  };

  // type NULL herite (avant BI-680) : modifiable meme si liee, pour pouvoir le renseigner
  const typeLocked = !!(
    editing && editing.oid && editing.environment_count > 0 && editing.provider_type
  );

  const columns = [
    { title: "Nom", dataIndex: "name", key: "name" },
    { title: "Type", dataIndex: "provider_type", key: "provider_type" },
    {
      title: "Modules",
      key: "modules",
      render: (t, f) => list(f.modules).map((m) => m.name).join(", "),
    },
    {
      title: "Dépôt",
      key: "repository",
      render: (t, f) => (f.repository ? repositoryLabel(f.repository) : ""),
    },
    {
      title: "Environnements",
      dataIndex: "environment_count",
      key: "environment_count",
      // raccourci vers la vue « Environnements associes », deja filtree
      render: (n, row) =>
        n ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => onShowEnvironments(row)}
          >
            {n}
          </Button>
        ) : (
          0
        ),
    },
    {
      ...ACTIONS_COLUMN,
      render: (t, row) => (
        <RowActions
          onEdit={() => openForm(row)}
          onDelete={() => handleDelete(row)}
        />
      ),
    },
  ];

  return (
    <div>
      <SettingsHeader title="Fonctionnalités" onAdd={() => openForm(null)} />
      <BarreVues
        view={view}
        setView={setView}
        nbCatalogue={features.length}
        nbEnvironnements={totalEnvironnements(data.features)}
        info={`${data.features.length} fonctionnalité${data.features.length > 1 ? "s" : ""} au catalogue`}
      />
      <div className="param-features-filtres" style={FILTRES}>
        <Select
          aria-label="Filtrer par type"
          placeholder="Tous les types"
          allowClear
          style={{ minWidth: 160 }}
          value={fType}
          onChange={setFType}
          options={TYPES}
        />
        <Select
          aria-label="Filtrer par module"
          placeholder="Tous les modules"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          value={fModule}
          onChange={setFModule}
          options={data.modules.map((m) => ({ value: m.oid, label: m.name }))}
        />
        <Input.Search
          aria-label="Recherche par nom"
          placeholder="Nom"
          allowClear
          style={{ width: 240 }}
          value={fNom}
          onChange={(e) => setFNom(e.target.value)}
        />
      </div>
      <Table
        rowKey="oid"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={features}
        pagination={false}
      />
      <Modal
        title={editing && editing.oid ? "Modifier" : "Ajouter"}
        open={!!editing}
        okText="Enregistrer"
        cancelText="Annuler"
        onOk={handleSubmit}
        onCancel={() => setEditing(null)}
        forceRender
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nom"
            rules={[{ required: true, whitespace: true, message: "Champ requis" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="provider_type"
            label="Type"
            rules={[{ required: true, message: "Champ requis" }]}
            tooltip={
              typeLocked
                ? "Type non modifiable : fonctionnalité liée à des environnements"
                : undefined
            }
          >
            <Select disabled={typeLocked} options={TYPES} />
          </Form.Item>
          <Form.Item
            name="module_ids"
            label="Modules"
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: "Au moins un module est requis",
              },
            ]}
          >
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={data.modules.map((m) => ({ value: m.oid, label: m.name }))}
            />
          </Form.Item>
          <Form.Item name="repository_id" label="Dépôt">
            <Select
              allowClear
              showSearch
              placeholder="Aucun"
              optionFilterProp="label"
              options={data.repositories.map((r) => ({
                value: r.oid,
                label: repositoryLabel(r),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

/* Environnements associes a une fonctionnalite : jamais la valeur de la cle.
 * Retrait de la liaison possible ici aussi (meme route que Gestion > environnement). */
// Statut de la liaison, derive des seules donnees de l'API (cle configuree, date d'expiration).
export const etatLiaison = (r) => {
  if (!r.key_configured) return "sans_cle";
  return r.expiration_date && moment(r.expiration_date).isBefore(moment(), "day") ? "expiree" : "configuree";
};

function Associated({ features, reload, view, setView, selected, setSelected }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fEnv, setFEnv] = useState("");
  const affichees = useMemo(() => {
    const q = sansAccent(fEnv.trim());
    return rows.filter((r) => !q || sansAccent(r.environment_label ?? r.finess_id).includes(q));
  }, [rows, fEnv]);
  const courante = features.find((f) => f.oid === selected);

  useEffect(() => {
    if (selected === undefined) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchFeatureEnvironments(selected)
      .then((r) => !cancelled && setRows(r))
      .catch((e) => !cancelled && message.error(errorMessage(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const unlink = async (row) => {
    try {
      await unlinkFeature(row.finess_id, row.oid);
      message.success("Liaison retirée");
      setRows((prev) => prev.filter((r) => r.oid !== row.oid));
      reload(); // nombre d'environnements et verrouillage du type au catalogue
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const columns = [
    {
      title: "Environnement",
      key: "env",
      render: (t, r) => r.environment_label ?? r.finess_id ?? "",
    },
    colonneEtat(etatLiaison),
    {
      title: "Expiration",
      dataIndex: "expiration_date",
      key: "expiration_date",
      render: (d) => (d ? moment(d).format("DD/MM/YYYY") : ""),
    },
    {
      ...ACTIONS_COLUMN,
      render: (t, r) => <RowActions onDelete={() => unlink(r)} />,
    },
  ];

  return (
    <div>
      <SettingsHeader title="Fonctionnalités" />
      <BarreVues
        view={view}
        setView={setView}
        nbCatalogue={features.length}
        nbEnvironnements={selected === undefined ? totalEnvironnements(features) : affichees.length}
        info={courante ? `${courante.name}${courante.provider_type ? ` · ${courante.provider_type}` : ""}` : undefined}
      />
      <div className="param-features-filtres" style={FILTRES}>
        <Select
          showSearch
          style={{ minWidth: 260 }}
          placeholder="Choisir une fonctionnalité"
          aria-label="Fonctionnalité"
          optionFilterProp="label"
          value={selected}
          onChange={setSelected}
          options={features.map((f) => ({ value: f.oid, label: f.name }))}
        />
        <Input.Search
          aria-label="Recherche d'environnement"
          placeholder="Environnement"
          allowClear
          style={{ width: 240 }}
          value={fEnv}
          onChange={(e) => setFEnv(e.target.value)}
        />
      </div>
      <Table
        rowKey="oid"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={affichees}
        pagination={false}
        locale={{ emptyText: selected ? "Aucun environnement associé" : "Choisissez une fonctionnalité" }}
      />
    </div>
  );
}

function ParamFeatures() {
  const [data, setData] = useState({ features: [], modules: [], repositories: [] });
  const [loading, setLoading] = useState(false);
  // vue (?view=) et fonctionnalite choisie (?feature=) dans l'URL : un lien les partage
  const [params, update] = useUrlParams();
  const view = params.view === "environments" ? "environments" : "catalog";
  const setView = (v) => update({ view: v });
  const selected = params.feature ? Number(params.feature) : undefined;
  const setSelected = (oid) => update({ feature: oid });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchFeatures());
    } catch (e) {
      message.error(errorMessage(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return view === "catalog" ? (
    <Declare
      data={data}
      loading={loading}
      reload={load}
      view={view}
      setView={setView}
      onShowEnvironments={(f) => update({ view: "environments", feature: f.oid })}
    />
  ) : (
    <Associated
      features={data.features}
      reload={load}
      view={view}
      setView={setView}
      selected={selected}
      setSelected={setSelected}
    />
  );
}

export default ParamFeatures;
